<template>
  <div class="fb-root">

    <!-- Expanded panel -->
    <Transition name="fb-slide">
      <div v-if="open" class="fb-panel">
        <!-- Submitted state -->
        <div v-if="submitted" class="fb-thankyou">
          <div class="fb-thankyou__icon">🙏</div>
          <p class="fb-thankyou__title">Thank you!</p>
          <p class="fb-thankyou__body">Your feedback means a lot and helps us keep improving.</p>
        </div>

        <!-- Form state -->
        <template v-else>
          <div class="fb-panel__header">
            <div>
              <p class="fb-panel__title">Share feedback</p>
              <p class="fb-panel__sub">What's on your mind? Any aspect of the site — we're all ears.</p>
            </div>
            <button class="fb-close-btn" @click="open = false" aria-label="Close feedback">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <textarea
            v-model="message"
            class="fb-textarea"
            placeholder="Tell us what you think…"
            rows="4"
            maxlength="2000"
          />

          <!-- Math captcha -->
          <div class="fb-captcha-row">
            <label class="fb-captcha-label">{{ captchaA }} + {{ captchaB }} =</label>
            <input
              v-model="captchaAnswer"
              type="number"
              class="fb-captcha-input"
              :class="{ 'fb-captcha-input--error': captchaError }"
              placeholder="?"
              autocomplete="off"
            />
            <span v-if="captchaError" class="fb-captcha-error">{{ captchaError }}</span>
          </div>

          <div class="fb-panel__footer">
            <span class="fb-char">{{ message.trim().length < 10 ? `${message.trim().length} / 10 min` : `${message.length} / 2000` }}</span>
            <button
              class="fb-submit-btn"
              :disabled="message.trim().length < 10 || !captchaAnswer || submitting"
              @click="submit"
            >
              {{ submitting ? 'Sending…' : 'Send' }}
            </button>
          </div>
          <p v-if="submitError" class="fb-error">{{ submitError }}</p>
        </template>
      </div>
    </Transition>

    <!-- Toggle tab -->
    <button class="fb-tab" :class="{ 'fb-tab--open': open }" @click="open = !open" aria-label="Toggle feedback">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
      <span>Feedback</span>
    </button>

  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { submitFeedback } from '@/services/feedbackService';
import { useAnalytics } from '@/composables/useAnalytics';

const userStore = useUserStore();
const { track } = useAnalytics();

const open = ref(false);
const message = ref('');
const submitting = ref(false);
const submitted = ref(false);
const submitError = ref('');

// ─── Math captcha ─────────────────────────────────────────────────────────────
const captchaA = ref(0);
const captchaB = ref(0);
const captchaAnswer = ref('');
const captchaError = ref('');

function newChallenge() {
  captchaA.value = Math.floor(Math.random() * 9) + 1;
  captchaB.value = Math.floor(Math.random() * 9) + 1;
  captchaAnswer.value = '';
  captchaError.value = '';
}

// Generate a challenge whenever the panel opens
watch(open, (val) => { if (val) newChallenge(); });

async function submit() {
  if (!message.value.trim()) return;

  captchaError.value = '';
  if (parseInt(captchaAnswer.value, 10) !== captchaA.value + captchaB.value) {
    captchaError.value = 'Incorrect answer — please try again.';
    newChallenge();
    return;
  }

  submitting.value = true;
  submitError.value = '';
  try {
    const session = userStore.session || {};
    await submitFeedback({
      message: message.value.trim(),
      uid:     session.uid   || null,
      name:    session.name  || null,
      email:   session.email || null,
    });
    submitted.value = true;
    track('feedback_submitted');
    // Auto-close after a moment so the thank-you is seen, then stays hidden.
    setTimeout(() => { open.value = false; }, 3000);
  } catch {
    submitError.value = 'Something went wrong — please try again.';
  } finally {
    submitting.value = false;
  }
}
</script>

<style>
/* Unscoped so the widget floats above all layout stacking contexts */
.fb-root {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  font-family: system-ui, sans-serif;
}

/* ── Toggle tab ── */
.fb-tab {
  display: flex;
  align-items: center;
  gap: 7px;
  background: #1A2332;
  color: #fff;
  border: none;
  border-radius: 9999px;
  padding: 10px 18px 10px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  transition: background 0.15s, transform 0.15s;
  letter-spacing: 0.01em;
}
.fb-tab:hover { background: #243044; transform: translateY(-1px); }
.fb-tab--open { background: #243044; }
.fb-tab svg { opacity: 0.85; }

/* ── Panel ── */
.fb-panel {
  width: 300px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.fb-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 16px 16px 0;
}
.fb-panel__title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 3px;
}
.fb-panel__sub {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
}
.fb-close-btn {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 2px;
  flex-shrink: 0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  transition: color 0.1s;
}
.fb-close-btn:hover { color: #374151; }

.fb-textarea {
  display: block;
  width: 100%;
  margin: 12px 0 0;
  padding: 10px 16px;
  border: none;
  border-top: 1px solid #f3f4f6;
  border-bottom: 1px solid #f3f4f6;
  font-size: 13px;
  font-family: inherit;
  color: #111827;
  resize: none;
  outline: none;
  background: #fafafa;
  box-sizing: border-box;
  line-height: 1.55;
}
.fb-textarea:focus { background: #fff; border-color: #d1d5db; }

.fb-panel__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px 14px;
}
.fb-char {
  font-size: 11px;
  color: #9ca3af;
}
.fb-submit-btn {
  background: #1A2332;
  color: #fff;
  border: none;
  border-radius: 7px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}
.fb-submit-btn:hover:not(:disabled) { opacity: 0.85; }
.fb-submit-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.fb-captcha-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  flex-wrap: wrap;
}
.fb-captcha-label {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}
.fb-captcha-input {
  width: 64px;
  padding: 5px 8px;
  font-size: 13px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  outline: none;
  text-align: center;
  background: #fff;
  color: #111827;
}
.fb-captcha-input:focus { border-color: #9ca3af; }
.fb-captcha-input--error { border-color: #ef4444; }
.fb-captcha-error {
  font-size: 11px;
  color: #ef4444;
  width: 100%;
  margin-top: 2px;
}

.fb-error {
  font-size: 12px;
  color: #ef4444;
  padding: 0 16px 12px;
  margin: 0;
}

/* ── Thank-you state ── */
.fb-thankyou {
  text-align: center;
  padding: 24px 20px;
}
.fb-thankyou__icon { font-size: 2rem; margin-bottom: 8px; }
.fb-thankyou__title { font-size: 15px; font-weight: 700; color: #111827; margin: 0 0 6px; }
.fb-thankyou__body { font-size: 13px; color: #6b7280; line-height: 1.5; margin: 0; }

/* ── Slide transition ── */
.fb-slide-enter-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.fb-slide-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.fb-slide-enter-from  { opacity: 0; transform: translateY(8px) scale(0.97); }
.fb-slide-leave-to    { opacity: 0; transform: translateY(8px) scale(0.97); }

/* ── Mobile: push away from bottom nav if present ── */
@media (max-width: 640px) {
  .fb-root { bottom: 80px; right: 16px; }
  .fb-panel { width: 280px; }
}
</style>
